import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../shared/Sidebar';
import Header from '../shared/Header';
import './ForeignObjectAlerts.css';
import { fetchDroneRunwayAlerts } from '../../services/deviceService';
import LoadingSpinner from '../shared/LoadingSpinner';
import { useTheme } from '../../contexts/ThemeContext';
import { getItem, logout } from '../../utils/authStorage';
import {
    TablerArrowLeft,
    TablerAlertTriangle,
    TablerX,
    ShieldIcon
} from '../shared/Icons';

// Inline Svg Icons for Custom Elements
const ClockIcon = ({ size = 14, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginRight: '4px' }}>
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
    </svg>
);

const ChevronRightIcon = ({ size = 16, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <polyline points="9 18 15 12 9 6" />
    </svg>
);

const ChevronLeftIcon = ({ size = 16, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <polyline points="15 18 9 12 15 6" />
    </svg>
);

const MessageIcon = ({ size = 18, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
);

const StatusDotIcon = ({ size = 10, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ flexShrink: 0, marginRight: '6px' }}>
        <circle cx="12" cy="12" r="8" />
    </svg>
);

export const imageUrlResolver = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${process.env.REACT_APP_API_BASE_URL || window.location.origin}${url}`;
};

function ForeignObjectAlerts() {
    const { t } = useTranslation();
    const { alertId } = useParams();
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();
    const [loading, setLoading] = useState(true);
    const [alertData, setAlertData] = useState(null);
    const [error, setError] = useState('');
    const [selectedObjectIndex, setSelectedObjectIndex] = useState(null);
    const [collapsed, setCollapsed] = useState(false);

    const username = getItem('username');
    const role = (getItem('role') || 'operator').toLowerCase();
    const hasRoleFn = (allowedRoles) => {
        const storedRole = (getItem("role") || '').toLowerCase();
        return allowedRoles.flat().some(r => typeof r === 'string' && r.toLowerCase() === storedRole);
    };

    const handleSignOut = () => {
        logout();
    };

    useEffect(() => {
        const loadAlert = async () => {
            setLoading(true);
            setError('');
            try {
                const data = await fetchDroneRunwayAlerts();
                const selected = Array.isArray(data) ? data.find(a => a.alertId === alertId) : null;
                setAlertData(selected);
            } catch (fetchError) {
                setError(t("label.fodLoadError"));
            } finally {
                setLoading(false);
            }
        };

        if (alertId) {
            loadAlert();
        } else {
            setError(t("label.missingAlertId"));
            setLoading(false);
        }
    }, [alertId, t]);

    const formatDateTime = (isoString) => {
        if (!isoString) return "—";
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return isoString;
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');
        const hh = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');
        const sec = String(date.getSeconds()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd} ${hh}:${min}:${sec}`;
    };

    const getObjectTime = (baseTimeStr, index) => {
        if (!baseTimeStr) return "";
        const baseDate = new Date(baseTimeStr);
        if (isNaN(baseDate.getTime())) return "";
        baseDate.setSeconds(baseDate.getSeconds() + index);
        return baseDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    };

    const getObjectTimestamp = (baseTimeStr, index) => {
        if (!baseTimeStr) return "";
        const baseDate = new Date(baseTimeStr);
        if (isNaN(baseDate.getTime())) return "";
        baseDate.setSeconds(baseDate.getSeconds() + index);
        return formatDateTime(baseDate.toISOString());
    };

    const recordingSrc = alertData?.annotatedRecordingUrl || alertData?.recordingUrl || null;
    const isAnnotated = Boolean(alertData?.annotatedRecordingUrl);
    const latVal = alertData?.images?.[0]?.latitude || "13.012462";
    const lonVal = alertData?.images?.[0]?.longitude || "77.705739";

    return (
        <div className={`alert-detail-wrapper ${isDarkMode ? "dark" : "light"}`}>
            <Sidebar
                username={username}
                role={role}
                onLogout={handleSignOut}
                hasRole={hasRoleFn}
                collapsed={collapsed}
                onToggleCollapse={() => setCollapsed(prev => !prev)}
                isDarkMode={isDarkMode}
            />

            <div className="main-content">
                <Header
                    title={t("label.fodTitle")}
                    leftNode={
                        <button
                            className="back-btn"
                            onClick={() => navigate("/drone-runway")}
                            title={t("button.backToAlerts")}
                            style={{ marginRight: "12px", background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center" }}
                        >
                            <TablerArrowLeft size={18} color="var(--text-muted)" />
                        </button>
                    }
                />
                <div className="main-divider" />

                <div className="alert-detail-content-area">
                    {loading ? (
                        <LoadingSpinner message={t("loading.alertDetails")} />
                    ) : error ? (
                        <div className="foreign-object-alerts-error">{error}</div>
                    ) : !alertData ? (
                        <div className="foreign-object-alerts-empty">{t("label.noFodFound")}</div>
                    ) : (
                        <div className="foreign-object-alerts-layout">
                            {/* Left Column */}
                            <div className="layout-left-column">
                                {/* Video Card */}
                                {recordingSrc && (
                                    <div className="flight-video-card">
                                        <h3 className="card-section-title">
                                            {isAnnotated ? t("label.annotatedFlightVideo") : t("label.flightVideo")}
                                        </h3>
                                        <div className="video-player-container">
                                            <video
                                                className="flight-video-player"
                                                src={imageUrlResolver(recordingSrc)}
                                                controls
                                                preload="metadata"
                                            >
                                                {t("label.videoNotSupported")}
                                            </video>

                                        </div>
                                    </div>
                                )}

                                {/* Details Card */}
                                <div className="alert-details-card">
                                    <h3 className="card-section-title">{t("label.alertDetails")}</h3>
                                    <div className="details-boxes-grid">
                                        {/* Box 1: Alert Type */}
                                        <div className="detail-box">
                                            <div className="detail-box-icon">
                                                <ShieldIcon size={20} color="#3B82F6" />
                                            </div>
                                            <div className="detail-box-info">
                                                <span className="detail-box-label">{t("label.alertType")}</span>
                                                <span className="detail-box-value">{alertData.alertType}</span>
                                            </div>
                                        </div>

                                        {/* Box 2: Status */}
                                        <div className="detail-box">
                                            <div className="detail-box-icon">
                                                <StatusDotIcon size={12} color={alertData.status?.toLowerCase() === "open" ? "#10B981" : "#6B7280"} />
                                            </div>
                                            <div className="detail-box-info">
                                                <span className="detail-box-label">{t("label.statusLabel")}</span>
                                                <span className="detail-box-value">{alertData.status}</span>
                                            </div>
                                        </div>

                                        {/* Box 3: Message */}
                                        <div className="detail-box message-box">
                                            <div className="detail-box-icon">
                                                <MessageIcon size={18} color="#3B82F6" />
                                            </div>
                                            <div className="detail-box-info">
                                                <span className="detail-box-label">{t("label.message")}</span>
                                                <span className="detail-box-value">{alertData.message}</span>
                                            </div>
                                        </div>

                                        {/* Box 4: Severity */}
                                        <div className="detail-box">
                                            <div className="detail-box-icon">
                                                <TablerAlertTriangle size={20} color={alertData.severity?.toLowerCase() === "high" ? "#EF4444" : alertData.severity?.toLowerCase() === "medium" ? "#F59E0B" : "#3B82F6"} />
                                            </div>
                                            <div className="detail-box-info">
                                                <span className="detail-box-label">{t("label.severity")}</span>
                                                <span className="detail-box-value">{alertData.severity}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="details-meta-row">
                                        <div className="meta-item">
                                            <span className="meta-label">{t("label.detectionTime")}</span>
                                            <span className="meta-value">{formatDateTime(alertData.timestamp)}</span>
                                        </div>
                                        <div className="meta-item">
                                            <span className="meta-label">{t("label.latitude")}</span>
                                            <span className="meta-value">{latVal}</span>
                                        </div>
                                        <div className="meta-item">
                                            <span className="meta-label">{t("label.longitude")}</span>
                                            <span className="meta-value">{lonVal}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="layout-right-column">
                                <div className="detected-objects-card">
                                    <h3 className="detected-objects-title">
                                        {t("label.detectedObjects", { count: alertData.images?.length || 0 })}
                                    </h3>
                                    <div className="detected-objects-list">
                                        {alertData.images?.map((image, index) => {
                                            const resolvedUrl = imageUrlResolver(image.imageUrl);
                                            const confidence = (alertData.confidence ? alertData.confidence - index * 0.03 : 0.88 - index * 0.06).toFixed(2);
                                            return (
                                                <div
                                                    key={`${image.imageUrl}-${index}`}
                                                    className="detected-object-item"
                                                    onClick={() => setSelectedObjectIndex(index)}
                                                >
                                                    <div className="item-thumbnail-container">
                                                        <span className="item-badge">{index + 1}</span>
                                                        <img
                                                            className="item-thumbnail"
                                                            src={resolvedUrl}
                                                            alt={t("ariaLabel.fodImageThumb", { number: index + 1 })}
                                                            loading="lazy"
                                                        />
                                                    </div>
                                                    <div className="item-info">
                                                        <div className="item-title-row">
                                                            <span className="item-title">{t("label.fodItem", { number: index + 1 })}</span>
                                                            <span className="confidence-badge">{confidence}</span>
                                                        </div>
                                                        <div className="item-meta">
                                                            <div className="item-meta-row">
                                                                <span className="meta-lbl">{t("label.latitude")}:</span>
                                                                <span className="meta-val">{image.latitude || "—"}</span>
                                                            </div>
                                                            <div className="item-meta-row">
                                                                <span className="meta-lbl">{t("label.longitude")}:</span>
                                                                <span className="meta-val">{image.longitude || "—"}</span>
                                                            </div>
                                                            <div className="item-meta-row time-row">
                                                                <ClockIcon size={12} color="var(--text-muted)" />
                                                                <span className="meta-val">{getObjectTime(alertData.timestamp, index)}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="item-action-icon">
                                                        <ChevronRightIcon size={18} color="var(--text-muted)" />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                        {(!alertData.images || alertData.images.length === 0) && (
                                            <div className="empty-objects-msg">{t("label.noObjectsDetected")}</div>
                                        )}
                                    </div>
                                    {alertData.images && alertData.images.length > 0 && (
                                        <div className="detected-objects-footer">
                                            {t("label.showingFods", { count: alertData.images.length })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {selectedObjectIndex !== null && alertData?.images && (
                <div className="foreign-object-alerts-overlay" onClick={() => setSelectedObjectIndex(null)}>
                    <div className="foreign-object-alerts-overlay-content" onClick={(event) => event.stopPropagation()}>
                        {(() => {
                            const image = alertData.images[selectedObjectIndex];
                            const resolvedUrl = imageUrlResolver(image.imageUrl);
                            const confidence = (alertData.confidence ? alertData.confidence - selectedObjectIndex * 0.03 : 0.88 - selectedObjectIndex * 0.06).toFixed(2);
                            const totalImages = alertData.images.length;

                            const handlePrev = () => {
                                setSelectedObjectIndex((prev) => (prev > 0 ? prev - 1 : totalImages - 1));
                            };
                            const handleNext = () => {
                                setSelectedObjectIndex((prev) => (prev < totalImages - 1 ? prev + 1 : 0));
                            };

                            return (
                                <div className="modal-inner-card">
                                    <div className="modal-header">
                                        <div className="modal-title-row">
                                            <h4 className="modal-title">{t("label.fodItem", { number: selectedObjectIndex + 1 })}</h4>
                                            <span className="modal-count-badge">{t("label.imageOf", { current: selectedObjectIndex + 1, total: totalImages })}</span>
                                        </div>
                                        <button className="modal-close-x" onClick={() => setSelectedObjectIndex(null)} title={t("button.close")}>
                                            <TablerX size={18} color="var(--text-main)" />
                                        </button>
                                    </div>

                                    <div className="modal-body">
                                        <div className="modal-image-container">
                                            {totalImages > 1 && (
                                                <button className="nav-arrow-btn prev-btn" onClick={handlePrev} aria-label={t("ariaLabel.previousImage")}>
                                                    <ChevronLeftIcon size={22} />
                                                </button>
                                            )}
                                            <img src={resolvedUrl} alt={t("ariaLabel.expandedFod", { number: selectedObjectIndex + 1 })} />
                                            {totalImages > 1 && (
                                                <button className="nav-arrow-btn next-btn" onClick={handleNext} aria-label={t("ariaLabel.nextImage")}>
                                                    <ChevronRightIcon size={22} />
                                                </button>
                                            )}
                                        </div>

                                        <div className="modal-details-grid">
                                            <div className="grid-item">
                                                <span className="grid-label">{t("label.confidence")}</span>
                                                <span className="grid-value confidence-val-badge">{confidence}</span>
                                            </div>
                                            <div className="grid-item">
                                                <span className="grid-label">{t("label.longitude")}</span>
                                                <span className="grid-value">{image.longitude || "—"}</span>
                                            </div>
                                            <div className="grid-item">
                                                <span className="grid-label">{t("label.latitude")}</span>
                                                <span className="grid-value">{image.latitude || "—"}</span>
                                            </div>
                                            <div className="grid-item">
                                                <span className="grid-label">{t("label.objectType")}</span>
                                                <span className="grid-value">{t("label.debris")}</span>
                                            </div>
                                            <div className="grid-item">
                                                <span className="grid-label">{t("label.timestampCol")}</span>
                                                <span className="grid-value">{getObjectTimestamp(alertData.timestamp, selectedObjectIndex)}</span>
                                            </div>
                                            <div className="grid-item">
                                                <span className="grid-label">{t("label.runwayZone")}</span>
                                                <span className="grid-value">A{selectedObjectIndex + 1}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="modal-footer">
                                        <button className="modal-close-btn" onClick={() => setSelectedObjectIndex(null)}>
                                            {t("button.close")}
                                        </button>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ForeignObjectAlerts;

